"use client";
import cx from "classnames";
import { type ReactNode, useContext, useState } from "react";
import shallow from "zustand/shallow";

import DeleteSongIcon from "@/components/DeleteSongIcon";
import HeaderSongViewMoreMenu from "@/components/HeaderSongViewMoreMenu";
import SavingOrLastSaved from "@/components/SavingOrLastSaved";
import SearchBar from "@/components/SearchBar";
import UserDrawer from "@/components/UserDrawer";
import { useAppStore } from "@/store/app";
import {
  getSongEditorRoute,
  getSongListRoute,
  getSongViewRoute,
} from "@/utils/getSongRoute";

import Link from "@/components/MyLink";
import { ServerContext } from "@/context/Server";
import { useReplicacheSetLists } from "@/store/replicache";
import useInfo from "@/utils/info";
import type { SetListId, SongId } from "@/utils/types";
import { useParams } from "next/navigation";
import { BsArrowLeft } from "react-icons/bs";
import { MdEdit } from "react-icons/md";
import { VscMenu } from "react-icons/vsc";

const LOG_TAG = "Header";

export default function Header({ children }: { children: ReactNode }) {
  const { session } = useContext(ServerContext);
  //const { songs } = useReplicacheSongs();
  const { setLists } = useReplicacheSetLists();
  const { isSearchOpen, setIsSearchOpen, setSearchKey, songs } = useAppStore(
    ({ isSearchOpen, setIsSearchOpen, setSearchKey, songs }) => ({
      isSearchOpen,
      setIsSearchOpen,
      setSearchKey,
      songs,
    }),
    shallow,
  );

  const {
    isLayoutDesktop,
    isLayoutPhone,
    isLayoutTablet,
    isRouteHome,
    isRouteSetList,
    isRouteSongEditor,
    isRouteSongPlay,
    isRouteSongView,
  } = useInfo();

  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);

  const { setListId, songId } = useParams() as {
    setListId: SetListId;
    songId: SongId;
  };

  const song = songId ? songs[songId] : null;
  const songTitle = song?.title;
  const songArtist = song?.artist;
  //console.debug(LOG_TAG, { songTitle, songArtist, songId });

  const toggleUserDrawer = () => {
    setIsUserDrawerOpen(!isUserDrawerOpen);
    window.analyticsLogEvent("Toggle UserDrawer", {
      isUserDrawerOpen: !isUserDrawerOpen,
    });
  };

  const isPhoneAndSongId = isLayoutPhone && song;
  //console.debug(LOG_TAG, { isPhoneAndSongId, isLayoutPhone });

  const showArrowBackButton =
    isSearchOpen || isPhoneAndSongId || isRouteSongEditor || isRouteSongPlay;

  const showSearchBar =
    !isPhoneAndSongId &&
    !isRouteSongEditor &&
    (isRouteHome || isRouteSongView || isRouteSetList);

  const showAppTitle = !showArrowBackButton && !isSearchOpen;

  const onClickBackButton = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false);
      setSearchKey("");
    }
  };

  const setList = setListId ? setLists[setListId] : undefined;
  const songListRoute = getSongListRoute({ setList });
  const backButtonHref =
    isRouteSongEditor && song
      ? getSongViewRoute({ song, setList })
      : songListRoute;

  const showSavingOrLastSaved =
    song && isRouteSongEditor && (isLayoutTablet || isLayoutDesktop);
  //console.debug(LOG_TAG, { showSavingOrLastSaved, song });

  return (
    <div className="drawer">
      <input id="drawer" type="checkbox" className="drawer-toggle" />
      <div className="drawer-content">
        <div className="navbar px-0 flex flex-row items-center bg-base-300 print:hidden">
          {showArrowBackButton ? (
            <Link
              href={backButtonHref}
              className="btn btn-ghost btn-circle"
              onClick={onClickBackButton}
            >
              <BsArrowLeft className="text-xl text-white font-bold" />
            </Link>
          ) : (
            <label htmlFor="drawer" className="btn btn-ghost btn-circle">
              <VscMenu className="text-xl text-accent-content" />
            </label>
          )}
          <div className="flex grow">
            {(showAppTitle || showSearchBar) && (
              <div className="flex justify-between w-full sm:w-[328px] sm:mr-[8px]">
                {showAppTitle && (
                  <div className="my-auto">
                    <h1 className="text-xl font-light text-accent-content">
                      <span className="font-normal">Songbook</span>.Studio
                    </h1>
                  </div>
                )}
                {showSearchBar && <SearchBar />}
              </div>
            )}
            <div
              className={cx("flex grow items-center justify-between", {
                "pl-6": !isLayoutPhone && !isRouteSongEditor && songTitle,
              })}
            >
              <div>
                {songTitle && (
                  <>
                    <h2
                      className={cx(
                        "text-xl font-normal cursor-pointer max-w-[100%]",
                        {
                          "font-medium": songTitle,
                        },
                      )}
                    >
                      {songTitle}
                    </h2>
                    {songArtist && (
                      <h3 className="text-sm text-accent-content">
                        {songArtist}
                      </h3>
                    )}
                  </>
                )}
              </div>
              <div className="flex flex-end items-center">
                {isRouteSongView && (
                  <>
                    <Link
                      className="btn btn-ghost btn-circle text-2xl text-white"
                      href={
                        session && song
                          ? getSongEditorRoute({ song, setList })
                          : "/signin"
                      }
                    >
                      <MdEdit />
                    </Link>
                    <HeaderSongViewMoreMenu />
                  </>
                )}
                {showSavingOrLastSaved && <SavingOrLastSaved song={song} />}
                {song && isRouteSongEditor && (
                  <DeleteSongIcon onlyIcon={true} />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex overflow-hidden h-[calc(100%-64px)] bg-base-200 print:bg-white print:overflow-auto print:h-auto">
          {children}
        </div>
      </div>
      <div className="drawer-side print:hidden">
        <label htmlFor="drawer" className="drawer-overlay" />
        <UserDrawer toggleUserDrawer={toggleUserDrawer} />
      </div>
    </div>
  );
}
